import { useCallback, useEffect, useRef, useState } from 'react';

import {
    DIGITS_PER_DRAW,
    REVEAL_LEAD_MS,
    REVEAL_LOCK_STEP_MS,
    REVEAL_SCRAMBLE_MS,
} from '../../../core/constants/lottery';
import { toDigits } from '../../../core/lottery/draw';

// Drives the ball reveal: every digit scrambles until its own lock timer
// fires, left to right. Starts fully revealed so a visitor landing between
// draws sees the standing result instead of an empty machine.

export function useReveal(targetNumber) {
    const [lockedCount, setLockedCount] = useState(DIGITS_PER_DRAW);
    const [scrambled, setScrambled] = useState(() => toDigits(targetNumber));
    const timers = useRef([]);

    const isSpinning = lockedCount < DIGITS_PER_DRAW;

    const clearTimers = useCallback(() => {
        timers.current.forEach(clearTimeout);
        timers.current = [];
    }, []);

    useEffect(() => clearTimers, [clearTimers]);

    useEffect(() => {
        if (!isSpinning) return undefined;
        const ticker = setInterval(() => {
            setScrambled(current => current.map(() => Math.floor(Math.random() * 10)));
        }, REVEAL_SCRAMBLE_MS);
        return () => clearInterval(ticker);
    }, [isSpinning]);

    const start = useCallback(() => {
        clearTimers();
        setLockedCount(0);
        timers.current = Array.from({ length: DIGITS_PER_DRAW }, (_, index) => setTimeout(
            () => setLockedCount(index + 1),
            REVEAL_LEAD_MS + index * REVEAL_LOCK_STEP_MS,
        ));
    }, [clearTimers]);

    const target = toDigits(targetNumber);
    const digits = target.map((digit, index) => ({
        value: index < lockedCount ? digit : scrambled[index],
        isLocked: index < lockedCount,
    }));

    return { digits, isSpinning, start };
}
